import type {
  ProductCardFragment,
  ProductVariantCardFragment,
} from '@boc/framework/saleor-graphql';

import { useProductDetails } from '~/framework/routes/products/providers';

interface ProductTagProps {
  variant?: ProductVariantCardFragment;
  product?: ProductCardFragment;
  tagsToDisplay?: number;
  showDiscount?: boolean;
  ProductDetails?: boolean;
}

export function calculateDiscountPercentage(
  originalPrice: number | null | undefined,
  salePrice: number | null | undefined
): number | null {
  if (!originalPrice || !salePrice || originalPrice <= salePrice) return null;

  const discountPercentage = Math.round(((originalPrice - salePrice) / originalPrice) * 100);

  return discountPercentage > 0 ? discountPercentage : null;
}

export function ProductTag({
  variant,
  product,
  tagsToDisplay = 1,
  showDiscount = false,
  ProductDetails = false,
}: ProductTagProps) {
  const productData = variant?.product || product;

  if (!productData) return null;

  const collectionWhiteList = ['new!', 'bestsellers', 'Frame-worthy!', 'top picks'];

  if (ProductDetails) {
    const whitelistedCollections =
      productData.collections?.filter((col: { name: string }) =>
        collectionWhiteList.some((whitelist) => col.name.toLowerCase() === whitelist.toLowerCase())
      ) || [];

    if (whitelistedCollections.length === 0) return null;

    return (
      <div className="flex flex-col gap-y-3 md:flex-row md:gap-y-0">
        <p className="md:basis-1/3">Collections</p>
        <div className="flex flex-wrap gap-2 md:basis-2/3">
          {whitelistedCollections.map((collection: { id: string; name: string }) => (
            <div
              key={collection.id}
              className="border-primary-2-80 bg-primary-2-20 bg-opacity-100 text-primary-2 rounded border px-4 py-2 text-xs font-medium uppercase"
            >
              {collection.name}
            </div>
          ))}
        </div>
      </div>
    );
  }

  let discountPercentage: number | null = null;

  if (showDiscount) {
    if (variant?.pricing?.onSale) {
      const originalPrice = variant.pricing?.priceUndiscounted?.gross?.amount;
      const salePrice = variant.pricing?.price?.gross?.amount;

      discountPercentage = calculateDiscountPercentage(originalPrice, salePrice);
    } else if (productData.pricing?.onSale) {
      const originalPrice = productData.pricing?.priceRangeUndiscounted?.start?.gross?.amount;
      const salePrice = productData.pricing?.priceRange?.start?.gross?.amount;

      discountPercentage = calculateDiscountPercentage(originalPrice, salePrice);
    }
  }

  const availableTags: Array<{ type: string; content: JSX.Element; name?: string }> = [];

  const isOutOfStock =
    !productData.isAvailable ||
    (variant && variant.quantityAvailable != null && variant.quantityAvailable <= 0);

  if (isOutOfStock) {
    availableTags.push({
      type: 'stock',
      content: (
        <div className="border-primary-2-80 bg-primary-2-20 bg-opacity-100 text-primary-2 absolute top-4 left-4 z-30 rounded border px-4 py-2 text-xs font-medium uppercase md:top-8 md:left-8">
          Out of Stock
        </div>
      ),
    });
  } else {
    const whitelistedCollection = productData.collections?.find((col: { name: string }) =>
      collectionWhiteList.some((whitelist) => col.name.toLowerCase() === whitelist.toLowerCase())
    );

    if (whitelistedCollection?.name) {
      availableTags.push({
        type: 'collection',
        content: (
          <div className="border-primary-2-80 bg-primary-2-20 bg-opacity-100 text-primary-2 absolute top-4 left-4 z-30 rounded border px-4 py-2 text-xs font-medium uppercase md:top-8 md:left-8">
            {whitelistedCollection.name}
          </div>
        ),
      });
    }
  }

  let discountBadge: JSX.Element | null = null;

  if (showDiscount && discountPercentage) {
    discountBadge = (
      <div className="border-primary-1-140 bg-primary-1-10 bg-opacity-100 text-primary-1-140 absolute top-4 right-4 z-30 rounded border px-4 py-2 text-xs font-medium uppercase md:top-8 md:right-8">
        {discountPercentage}% Off
      </div>
    );
  }

  const tagsToRender = availableTags.slice(0, tagsToDisplay);

  if (tagsToRender.length === 0 && !discountBadge) return null;

  if (tagsToDisplay > 1 && tagsToRender.length > 0) {
    return (
      <>
        <div className="absolute top-4 left-4 z-30 flex gap-2 md:top-8 md:left-8">
          {tagsToRender.map((tag, index) => (
            <div
              key={`${tag.type}-${index}`}
              className="border-primary-2-80 bg-primary-2-20 bg-opacity-100 text-primary-2 rounded border px-4 py-2 text-xs font-medium uppercase"
            >
              {tag.type === 'stock' && 'Out of Stock'}
              {tag.type === 'collection' && tag.name}
            </div>
          ))}
        </div>
        {discountBadge && <div key="discount">{discountBadge}</div>}
      </>
    );
  }

  return (
    <>
      {tagsToRender.map((tag, index) => (
        <div key={`${tag.type}-${index}`}>{tag.content}</div>
      ))}
      {discountBadge && <div key="discount">{discountBadge}</div>}
    </>
  );
}

export function getProductDiscountPercentage(product: ProductCardFragment): number | null {
  if (!product.pricing?.onSale) return null;

  const originalPrice = product.pricing?.priceRangeUndiscounted?.start?.gross?.amount;
  const salePrice = product.pricing?.priceRange?.start?.gross?.amount;

  return calculateDiscountPercentage(originalPrice, salePrice);
}

export function DiscountBadge() {
  const { variant } = useProductDetails();

  if (!variant.pricing?.onSale) return null;

  const originalPrice = variant.pricing?.priceUndiscounted?.gross?.amount;
  const salePrice = variant.pricing?.price?.gross?.amount;

  const discountPercentage = calculateDiscountPercentage(originalPrice, salePrice);

  if (!discountPercentage) return null;

  return (
    <div className="border-primary-1-140 bg-primary-1-10 bg-opacity-100 text-primary-1-140 inline-flex w-fit rounded border px-4 py-2 text-xs leading-3 font-semibold tracking-wider uppercase">
      {discountPercentage}% Off
    </div>
  );
}
